import dayjs from 'dayjs';

/**
 * 스케줄에 달린 회원의 앱 연동 여부 / 수업일지 작성 여부를 판정.
 *
 * 관리자는 "수업일지 미작성" 회원을 볼 때마다 회원 목록으로 이동해 연동 회원인지
 * 확인해야 했다. 스케줄 응답에 이미 들어있는 값만으로 두 상태를 함께 보여주기 위한 유틸.
 */

export type ScheduleAppLinkState = 'linked' | 'withdrawn' | 'unlinked' | 'unknown';
export type ScheduleClassLogState = 'written' | 'missing' | 'none';

export type ScheduleMemberLinkSource = {
    /** 회원관리와 동일한 3상태. 탈퇴와 단순 미연동을 구분하기 위해 최우선 사용 */
    appAccountStatus?: 'linked' | 'withdrawn' | 'unlinked';
    appWithdrawnAt?: string | null;
    appWithdrawalAuthStatus?: 'pending' | 'revoked' | null;
    /** 탈퇴 회원의 변경을 차단하기 위해 서버가 직접 내려주는 읽기 전용 플래그 */
    isReadOnly?: boolean;
    /** 구형 백엔드 호환 필드 */
    appLinked?: boolean;
    memberAppLinked?: boolean;
    /** 스케줄 생성 시점의 회원 타입 스냅샷. 임시회원이 앱 연동되면 original 로 일괄 갱신된다 */
    memberType?: string;
    postId?: string | null;
    attendanceStatus?: string;
    endTime?: string;
} | null | undefined;

/**
 * 스케줄 응답만으로 앱 계정 상태를 확정할 수 있는지 확인한다.
 * 구형 memberType 스냅샷은 탈퇴 후에도 original 일 수 있어 확정 근거로 쓰지 않는다.
 */
export const hasExplicitScheduleAppAccountStatus = (schedule: ScheduleMemberLinkSource): boolean =>
    !!schedule &&
    (schedule.appAccountStatus === 'linked' ||
        schedule.appAccountStatus === 'withdrawn' ||
        schedule.appAccountStatus === 'unlinked' ||
        !!schedule.appWithdrawnAt ||
        typeof schedule.isReadOnly === 'boolean');

export const SCHEDULE_APP_LINK_LABEL: Record<Exclude<ScheduleAppLinkState, 'unknown'>, string> = {
    linked: '앱 연동',
    withdrawn: '앱 탈퇴',
    unlinked: '앱 미연동',
};

export const SCHEDULE_CLASS_LOG_LABEL: Record<Exclude<ScheduleClassLogState, 'none'>, string> = {
    written: '일지 작성',
    missing: '일지 미작성',
};

/**
 * 앱 연동 여부.
 * 관리자가 등록한 회원은 temp(PT)/public(퍼블릭) 으로 생성되고, 회원이 앱에 가입하면
 * 스케줄까지 original 로 이관되므로 memberType 을 연동 여부의 기준으로 쓸 수 있다.
 */
export const resolveScheduleAppLink = (schedule: ScheduleMemberLinkSource): ScheduleAppLinkState => {
    if (!schedule) return 'unknown';

    if (
        schedule.appAccountStatus === 'linked' ||
        schedule.appAccountStatus === 'withdrawn' ||
        schedule.appAccountStatus === 'unlinked'
    ) {
        return schedule.appAccountStatus;
    }

    // 과도기 응답은 3상태 필드 없이 탈퇴 시각만 내려올 수 있다.
    // 명시적 상태보다 뒤, legacy boolean보다 앞에서 판정해야 탈퇴를 단순 미연동으로 잃지 않는다.
    if (schedule.appWithdrawnAt) return 'withdrawn';

    const explicit = schedule.appLinked ?? schedule.memberAppLinked;
    if (typeof explicit === 'boolean') return explicit ? 'linked' : 'unlinked';

    switch (schedule.memberType) {
        case 'original':
            return 'linked';
        case 'temp':
        case 'public':
            return 'unlinked';
        default:
            return 'unknown';
    }
};

/** 탈퇴 회원 스케줄에 적용할 공통 읽기 전용 판정 */
export const isWithdrawnScheduleReadOnly = (schedule: ScheduleMemberLinkSource): boolean =>
    schedule?.isReadOnly === true ||
    schedule?.appWithdrawalAuthStatus === 'pending' ||
    resolveScheduleAppLink(schedule) === 'withdrawn';

/**
 * 수업일지 작성 여부.
 * 아직 끝나지 않은 수업과 노쇼 수업은 미작성이 정상이므로 배지를 띄우지 않는다.
 */
export const resolveScheduleClassLog = (
    schedule: ScheduleMemberLinkSource,
    now: Date | string | number = new Date()
): ScheduleClassLogState => {
    if (!schedule) return 'none';
    if (schedule.postId) return 'written';
    if (schedule.attendanceStatus === 'NOSHOW') return 'none';
    if (!schedule.endTime) return 'none';
    return dayjs(schedule.endTime).isBefore(dayjs(now)) ? 'missing' : 'none';
};
